import {
  CustomBuydownPlan,
  CustomBuydownSwitchResult,
  CustomBuydownSwitchStatus,
  CustomBuydownTermRate,
} from '@src/types/CustomBuydown'
import { keepPreviousData, QueryFunctionContext, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { getApiClient } from '../api-client'

const SCOPE = 'custom-buydown'
const PLANS = 'plans'
const TERM_RATES = 'term-rates'
const SWITCH_STATUS = 'switch-status'

const keysFactory = {
  all: () => [{ scope: SCOPE }] as const,
  plans: (merchantId: string) => [{ scope: SCOPE, entity: PLANS, merchantId }] as const,
  switchStatus: (merchantId: string) => [{ scope: SCOPE, entity: SWITCH_STATUS, merchantId }] as const,
  termRates: (merchantId: string, merchantFeeRate: number, borrowerFeeRate: number, maxFinancingAmount?: number) =>
    [{ scope: SCOPE, entity: TERM_RATES, merchantId, merchantFeeRate, borrowerFeeRate, maxFinancingAmount }] as const,
}

const getCustomBuydownSwitchStatus = async ({
  queryKey: [{ merchantId }],
}: QueryFunctionContext<ReturnType<(typeof keysFactory)['switchStatus']>>) => {
  const apiClient = getApiClient()
  const response = await apiClient.get<CustomBuydownSwitchStatus>(
    `/Merchant/${encodeURIComponent(merchantId)}/CustomBuydown/SwitchStatus`,
  )
  return response.data
}

export function useCustomBuydownSwitchStatusQuery(merchantId: string) {
  return useQuery({
    queryKey: keysFactory.switchStatus(merchantId),
    queryFn: getCustomBuydownSwitchStatus,
    enabled: !!merchantId,
  })
}

const requestCustomBuydownSwitch = async ({
  merchantId,
  contactEmails,
}: {
  merchantId: string
  contactEmails: string[]
}) => {
  const apiClient = getApiClient()
  const response = await apiClient.post<CustomBuydownSwitchResult>(
    `/Merchant/${encodeURIComponent(merchantId)}/CustomBuydown/SwitchRequest`,
    { contactEmails },
  )
  return response.data
}

export function useRequestCustomBuydownSwitch(merchantId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (contactEmails: string[]) => requestCustomBuydownSwitch({ merchantId, contactEmails }),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: keysFactory.switchStatus(merchantId) }),
  })
}

const getCustomBuydownPlans = async ({
  queryKey: [{ merchantId }],
}: QueryFunctionContext<ReturnType<(typeof keysFactory)['plans']>>) => {
  const apiClient = getApiClient()
  const response = await apiClient.get<CustomBuydownPlan[]>(
    `/Merchant/${encodeURIComponent(merchantId)}/CustomBuydown`,
  )
  return response.data
}

export function useCustomBuydownPlansQuery(merchantId: string) {
  return useQuery({
    queryKey: keysFactory.plans(merchantId),
    queryFn: getCustomBuydownPlans,
    enabled: !!merchantId,
  })
}

export interface CreateCustomBuydownPlanDto {
  contribution: number
  maxFinancingAmount?: number
  versionTag?: string
}

const createCustomBuydownPlan = async ({
  merchantId,
  dto,
}: {
  merchantId: string
  dto: CreateCustomBuydownPlanDto
}) => {
  const apiClient = getApiClient()
  const response = await apiClient.post<CustomBuydownPlan>(
    `/Merchant/${encodeURIComponent(merchantId)}/CustomBuydown`,
    dto,
  )
  return response.data
}

export function useCreateCustomBuydownPlan(merchantId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (dto: CreateCustomBuydownPlanDto) => createCustomBuydownPlan({ merchantId, dto }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: keysFactory.plans(merchantId) })
    },
  })
}

const getCustomBuydownTermRates = async ({
  queryKey: [{ merchantId, merchantFeeRate, borrowerFeeRate, maxFinancingAmount }],
}: QueryFunctionContext<ReturnType<(typeof keysFactory)['termRates']>>) => {
  const apiClient = getApiClient()
  const response = await apiClient.get<CustomBuydownTermRate[]>(
    `/Merchant/${encodeURIComponent(merchantId)}/CustomBuydown/TermRates`,
    { params: { merchantFeeRate, borrowerFeeRate, maxFinancingAmount } },
  )
  return response.data
}

export function useCustomBuydownTermRates(
  merchantId: string,
  merchantFeeRate: number,
  borrowerFeeRate: number,
  maxFinancingAmount?: number,
) {
  return useQuery({
    queryKey: keysFactory.termRates(merchantId, merchantFeeRate, borrowerFeeRate, maxFinancingAmount),
    queryFn: getCustomBuydownTermRates,
    enabled: !!merchantId && merchantFeeRate > 0,
    placeholderData: keepPreviousData,
  })
}

const terminateCustomBuydownPlan = async ({ merchantId, promoPlanId }: { merchantId: string; promoPlanId: string }) => {
  const apiClient = getApiClient()
  await apiClient.post(
    `/Merchant/${encodeURIComponent(merchantId)}/CustomBuydown/${encodeURIComponent(promoPlanId)}/Terminate`,
  )
}

export function useTerminateCustomBuydownPlan(merchantId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (promoPlanId: string) => terminateCustomBuydownPlan({ merchantId, promoPlanId }),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: keysFactory.plans(merchantId) }),
  })
}

